import { html } from 'lit';
import type { LandingEditorElementDefinition } from '../editor/landingEditorRegistry.js';
import type { LandingHomeSectionKey } from '../../shared/editor/landingEditorTypes.js';
import {
  landingEditorSectionLibraryGroups,
  type LandingEditorSectionLibraryEntry,
} from '../../shared/editor/landingEditorSectionLibrary.js';

type PathDefinition = LandingEditorElementDefinition & { path: string };

interface EditorAssetsViewOptions {
  sections: readonly LandingHomeSectionKey[];
  orderedSections: readonly LandingHomeSectionKey[];
  mediaDefinitions: readonly LandingEditorElementDefinition[];
  selectedFieldDefinition: PathDefinition | null;
  isSelectedMedia: boolean;
  fieldValueForDefinition: (definition: PathDefinition) => string;
  addSection: (section: LandingHomeSectionKey) => void;
  setSelectedElementId: (elementId: string) => void;
  handleSectionDragStart: (event: DragEvent, section: LandingHomeSectionKey, intent?: 'copy' | 'move') => void;
  setDraggedSection: (section: LandingHomeSectionKey | null) => void;
  renderLayerRow: (definition: LandingEditorElementDefinition, depth?: number) => unknown;
  mediaLabelForKind: (kind: LandingEditorElementDefinition['kind']) => string;
  mediaAcceptForKind: (kind: LandingEditorElementDefinition['kind']) => string;
  handleMediaUpload: (definition: PathDefinition, event: Event) => void;
  renderMediaPreview: (definition: PathDefinition) => unknown;
}

const previewImageDefinition = (
  section: LandingHomeSectionKey,
  definitions: readonly LandingEditorElementDefinition[],
) => definitions.find(
  (definition): definition is PathDefinition =>
    definition.section === section &&
    definition.kind === 'image' &&
    typeof definition.path === 'string' &&
    !/(?:logo|icon)/iu.test(definition.id),
);

const renderMiniature = (entry: LandingEditorSectionLibraryEntry, imageUrl: string) => html`
  <span class="vp-editor-section-thumbnail is-${entry.previewLayout} is-category-${entry.category}" aria-hidden="true">
    <span class="vp-editor-section-thumbnail-art">
      ${imageUrl
        ? html`<img
            src=${imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            draggable="false"
            @error=${(event: Event) => (event.currentTarget as HTMLImageElement).remove()}
          />`
        : ''}
      <span class="${entry.icon} vp-editor-section-thumbnail-icon"></span>
    </span>
    <span class="vp-editor-section-thumbnail-copy">
      <i></i><i></i><i></i>
    </span>
  </span>
`;

export const renderEditorAssetsPanel = (options: EditorAssetsViewOptions) => {
  const availableSections = new Set(options.sections);
  const presentSections = new Set(options.orderedSections);
  const usedCount = options.sections.filter(section => presentSections.has(section)).length;

  const renderSection = (entry: LandingEditorSectionLibraryEntry) => {
    const isPresent = presentSections.has(entry.key);
    const imageDefinition = previewImageDefinition(entry.key, options.mediaDefinitions);
    const imageUrl = imageDefinition ? options.fieldValueForDefinition(imageDefinition) : '';
    const action = isPresent ? 'Auf der Seite auswählen' : 'Zur Seite hinzufügen';
    return html`
      <button
        type="button"
        class="vp-editor-section-library-item ${isPresent ? 'is-used' : ''}"
        draggable="true"
        aria-label="${entry.label}: ${action}"
        @click=${() => isPresent
          ? options.setSelectedElementId(`section.${entry.key}`)
          : options.addSection(entry.key)}
        @dragstart=${(event: DragEvent) => {
          (event.currentTarget as HTMLElement).classList.add('is-dragging');
          options.handleSectionDragStart(event, entry.key, isPresent ? 'move' : 'copy');
        }}
        @dragend=${(event: DragEvent) => {
          (event.currentTarget as HTMLElement).classList.remove('is-dragging');
          options.setDraggedSection(null);
        }}
      >
        ${renderMiniature(entry, imageUrl)}
        <span class="vp-editor-section-library-copy">
          <strong>${entry.label}</strong>
          <small>${entry.archived ? 'Archiviert · ' : ''}${isPresent ? 'Auf dieser Seite' : 'Verfügbar'}</small>
        </span>
        <span
          class="${isPresent
            ? 'icon-[material-symbols--check-circle-outline]'
            : 'icon-[material-symbols--add-circle-outline]'} vp-editor-section-library-action"
        ></span>
      </button>
    `;
  };

  return html`
    <section class="vp-editor-panel-section vp-editor-section-library">
      <header class="vp-editor-section-library-header">
        <span>
          <strong>Section-Bibliothek</strong>
          <small>${options.sections.length} Vorlagen</small>
        </span>
        <span class="vp-editor-section-library-count">${usedCount} verwendet</span>
      </header>
      <p class="vp-editor-hint">Klicken zum Einfügen oder an die gewünschte Position der aktuellen Seite ziehen.</p>
      <div class="vp-editor-section-library-groups">
        ${landingEditorSectionLibraryGroups.map(group => {
          const entries = group.entries.filter(entry => availableSections.has(entry.key));
          if (!entries.length) return '';
          return html`
            <section class="vp-editor-section-library-group" aria-label=${group.label}>
              <h3>${group.label}</h3>
              <div class="vp-editor-section-library-list">${entries.map(renderSection)}</div>
            </section>
          `;
        })}
      </div>
    </section>
    <section class="vp-editor-panel-section">
      <p class="vp-editor-kicker">Medien</p>
      ${options.mediaDefinitions.map(definition => options.renderLayerRow(definition, 0))}
    </section>
    ${options.selectedFieldDefinition && options.isSelectedMedia
      ? html`
          <section class="vp-editor-panel-section">
            <label>
              ${options.mediaLabelForKind(options.selectedFieldDefinition.kind)} Upload
              <input
                type="file"
                accept=${options.mediaAcceptForKind(options.selectedFieldDefinition.kind)}
                @change=${(event: Event) => options.handleMediaUpload(options.selectedFieldDefinition!, event)}
              />
            </label>
            ${options.renderMediaPreview(options.selectedFieldDefinition)}
          </section>
        `
      : ''}
  `;
};
